import { useEffect, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { isNil } from "lodash";

import { EquipmentTypeMultiSelect } from "components/EquipmentTypeSelect";
import { ErrorTypography } from "components/ErrorTypography";
import { EvotruxDivider } from "components/EvotruxDivider";
import { NumberInput } from "components/NumberInput";
import { ServiceTypeSelect } from "components/ServiceTypeSelect";
import { UpdatePaymenInfoLabel } from "components/UpdatePaymentInfoLabel";
import { shipmentTypeSupportsPublicSharing } from "utils";

import { BookNowMenu } from "../BookNowMenu";
import { EmailNotificationPreferences } from "../EmailNotificationPreferences";
import { PricingUnitOfMeasureMenu } from "../PricingUnitOfMeasureMenu";
import { ShipmentTypeMenu } from "../ShipmentTypeMenu";
import { AdvancedOptionCard } from "./components/AdvancedOptionCard";
import { ConsolidatedShipmentVisibilityContainer } from "./components/ConsolidatedShipmentVisibility";
import { FileUpload } from "./components/FileUpload";
import { FreetextCommodityInput } from "./components/FreetextCommodityInput";
import { ShareShipmentMenu } from "./components/ShareShipmentMenu";
import { ShipmentReviewModal } from "./components/ShipmentReviewModal";
import { UnifiedCustomerSelectorContainer } from "./components/UnifiedCustomerSelector";
import { useExternallyDrivenExpansion } from "./hooks";

import type { SelectChangeEvent } from "@mui/material";
import type { ReactNode } from "react";

import type { CustomerListItem } from "components/SupplierNetwork/types";
import type { CreateShipmentFormState } from "types/shipment";

import type { UnifiedPartnerSelectorRef } from "./components/ConsolidatedShipmentVisibility/components/UnifiedPartnerSelector";
import type { DirectBookingSectionProps } from "./components/ConsolidatedShipmentVisibility/ConsolidatedShipmentVisibility";

function SectionDivider() {
  return (
    <Box sx={{ my: 4 }}>
      <EvotruxDivider />
    </Box>
  );
}

const LB_PER_KG = 2.20462;
const pillButtonSx = {
  borderRadius: "20px",
  borderColor: "grey.400",
  color: "text.primary",
  "&:hover": { borderColor: "grey.600" },
};

interface Props {
  routePoints: React.ReactNode[];
  equipmentTypes: string[];
  serviceType: string;
  weightLb: number | null;
  specialRequirements: string;
  additionalDetails: string;
  error: string | null;
  onValueChanged: <T>(
    key: keyof CreateShipmentFormState,
    value: T | null,
  ) => void;
  toggleCreateRouteOpen: VoidFunction;
  onSubmit: VoidFunction;
  submitLoading: boolean;
  setShipmentNotificationType: (type: ShipmentNotificationType) => void;
  setShipmentNotificationEmails: (emails: string[]) => void;
  commodityFreetext: string;
  shipmentNotificationType: ShipmentNotificationType;
  selectedCarriers: string[];
  createTemplateButton: ReactNode;
  showBookNow: boolean;
  bookNowPrice: string;
  bookNowCurrency: string;
  customName: string;
  emailNotificationType: ShipperEmailNotificationType;
  showShareMenu?: boolean;
  setShareeRoleIds: (roleIds: number[]) => void;
  selectedSharees: number[];
  shipmentType: ShipmentType;
  availableLoads: number | null;
  files: File[];
  onFileErrors: (errors: string[]) => void;
  formState: CreateShipmentFormState;
  showReviewModal: boolean;
  onReviewModalClose: () => void;
  onConfirmSubmit: () => void;
  onCustomerSelectionChange: (data: {
    customerRoleIds: number[];
    externalCustomerIds: number[];
    customerInviteEmails: string[];
  }) => void;
  supplierCustomers: CustomerListItem[];
  quotingUnitOfMeasure: UnitOfMeasureType;
  // Legacy handlers for partner groups and network invites
  setPartnerGroups: (groups: string[]) => void;
  setNetworkInviteEmails: (emails: string[]) => void;
  onEmailInputRef: (ref: UnifiedPartnerSelectorRef) => void;
  // Direct booking
  directBooking: DirectBookingSectionProps;
  onDirectBookingSelected: () => void;
  onDirectBookingCleared: () => void;
  quotesOnlyDisabledReason: string | null;
  equipmentError: string | null;
}

// TODO: Manage large number of props and intense prop drilling
export function CreateShipmentForm(props: Props) {
  const {
    error,
    routePoints,
    equipmentTypes,
    serviceType,
    weightLb,
    specialRequirements,
    additionalDetails,
    onSubmit,
    onValueChanged,
    toggleCreateRouteOpen,
    submitLoading,
    setShipmentNotificationType,
    setShipmentNotificationEmails,
    commodityFreetext,
    shipmentNotificationType,
    selectedCarriers,
    createTemplateButton,
    showBookNow,
    bookNowCurrency,
    bookNowPrice,
    customName,
    emailNotificationType,
    showShareMenu = false,
    setShareeRoleIds,
    selectedSharees,
    shipmentType,
    availableLoads,
    files,
    onFileErrors,
    formState,
    showReviewModal,
    onReviewModalClose,
    onConfirmSubmit,
    onCustomerSelectionChange,
    supplierCustomers,
    quotingUnitOfMeasure,
    setPartnerGroups,
    setNetworkInviteEmails,
    onEmailInputRef,
    directBooking,
    onDirectBookingSelected,
    onDirectBookingCleared,
    quotesOnlyDisabledReason,
    equipmentError,
  } = props;

  const [weightUnit, setWeightUnit] = useState<"lb" | "kg">("lb");
  const [displayWeight, setDisplayWeight] = useState<number | null>(weightLb);
  const expectedWeightLb = useRef<number | null>(weightLb);

  useEffect(() => {
    if (weightLb !== expectedWeightLb.current) {
      expectedWeightLb.current = weightLb;
      // Intentionally resets to lb on every external update (template/copy) because
      // the unit is not persisted. A follow-up to store the unit on the model would
      // let us preserve the user's selection here.
      setWeightUnit("lb");
      setDisplayWeight(weightLb);
    }
  }, [weightLb]);

  const handleWeightChange = (val: number | null) => {
    setDisplayWeight(val);
    if (isNil(val)) {
      expectedWeightLb.current = null;
      onValueChanged<number | null>("weightLb", null);
      return;
    }
    if (weightUnit === "kg") {
      const lbVal = Math.round(val * LB_PER_KG * 100) / 100;
      expectedWeightLb.current = lbVal;
      onValueChanged<number>("weightLb", lbVal);
    } else {
      expectedWeightLb.current = val;
      onValueChanged<number>("weightLb", val);
    }
  };

  const handleWeightUnitChange = (newUnit: "lb" | "kg") => {
    if (newUnit === weightUnit) return;
    if (newUnit === "kg") {
      setDisplayWeight(
        isNil(displayWeight)
          ? null
          : Math.round((displayWeight / LB_PER_KG) * 100) / 100,
      );
    } else {
      // Restore from the authoritative lb value rather than re-converting the rounded kg
      // display, which would compound rounding errors (e.g. 152 lb → 68.95 kg → 152.01 lb).
      setDisplayWeight(expectedWeightLb.current);
    }
    setWeightUnit(newUnit);
  };

  const {
    expanded: bookNowExpanded,
    autoFocus: bookNowAutoFocus,
    expand: expandBookNow,
    collapse: collapseBookNow,
    markLocalChange: markBookNowLocal,
  } = useExternallyDrivenExpansion(bookNowPrice, (val) => val !== "");
  const {
    expanded: specialRequirementsExpanded,
    autoFocus: specialRequirementsAutoFocus,
    expand: expandSpecialRequirements,
    collapse: collapseSpecialRequirements,
    markLocalChange: markSpecialRequirementsLocal,
  } = useExternallyDrivenExpansion(specialRequirements, (val) => val !== "");
  const {
    expanded: additionalDetailsExpanded,
    autoFocus: additionalDetailsAutoFocus,
    expand: expandAdditionalDetails,
    collapse: collapseAdditionalDetails,
    markLocalChange: markAdditionalDetailsLocal,
  } = useExternallyDrivenExpansion(additionalDetails, (val) => val !== "");
  const {
    expanded: referenceExpanded,
    autoFocus: referenceAutoFocus,
    expand: expandReference,
    collapse: collapseReference,
    markLocalChange: markReferenceLocal,
  } = useExternallyDrivenExpansion(customName, (val) => val !== "");
  const {
    expanded: shareExpanded,
    autoFocus: shareAutoFocus,
    expand: expandShare,
    collapse: collapseShare,
    markLocalChange: markShareesLocal,
  } = useExternallyDrivenExpansion(selectedSharees, (val) => val.length > 0);
  const {
    expanded: filesExpanded,
    expand: expandFiles,
    collapse: collapseFiles,
    markLocalChange: markFilesLocal,
  } = useExternallyDrivenExpansion(files, (val) => val.length > 0);

  const onEquipmentTypeChanged = (newValue: string[]) => {
    onValueChanged<string[]>("equipmentTypes", newValue);
  };
  const onServiceChanged = (newValue: string) => {
    onValueChanged<string>("service", newValue);
    if (newValue === "TRUCK_LOAD") {
      onValueChanged<string>("footage", "");
    }
    if (newValue === "LESS_THAN_TRUCK_LOAD") {
      expandAdditionalDetails();
    }
  };

  const onFreetextCommodityChanged = (newValue: string) => {
    onValueChanged<string>("commodityFreetext", newValue);
  };

  const handleSpecialRequirementsChange = (val: string) => {
    markSpecialRequirementsLocal(val);
    onValueChanged<string>("specialRequirements", val);
  };

  const handleAdditionalDetailsChange = (val: string) => {
    markAdditionalDetailsLocal(val);
    onValueChanged<string>("additionalDetails", val);
  };

  const handleCustomNameChange = (val: string) => {
    markReferenceLocal(val);
    onValueChanged<string>("customName", val);
  };

  const handleBookNowPriceChange = (val: string) => {
    markBookNowLocal(val);
    onValueChanged<string>("bookNowPrice", val);
  };

  // Closing an advanced-option card clears its value rather than only collapsing the card.
  // Otherwise a value typed in — or carried over from a copied shipment — silently persists into
  // review and posting even though the user removed it. Share has its own handler below because
  // clearing it must also reset the companion shareShipment flag, which onValueChanged does not
  // manage (clearing bookNowPrice does cascade to isBookNow via the container).
  const closeAdvancedOption =
    <T,>(
      key: keyof CreateShipmentFormState,
      emptyValue: T,
      collapse: VoidFunction,
    ) =>
    () => {
      onValueChanged<T>(key, emptyValue);
      collapse();
    };

  const handleBookNowClose = closeAdvancedOption<string>(
    "bookNowPrice",
    "",
    collapseBookNow,
  );
  const handleSpecialRequirementsClose = closeAdvancedOption<string>(
    "specialRequirements",
    "",
    collapseSpecialRequirements,
  );
  const handleAdditionalDetailsClose = closeAdvancedOption<string>(
    "additionalDetails",
    "",
    collapseAdditionalDetails,
  );
  const handleReferenceClose = closeAdvancedOption<string>(
    "customName",
    "",
    collapseReference,
  );
  // Files post regardless of the card being collapsed (the upload path is gated only on
  // files.length), so closing must clear them too — otherwise staged uploads silently attach to
  // the posted shipment. Individual files can still be removed via the per-file delete button.
  const handleFilesClose = closeAdvancedOption<File[]>(
    "files",
    [],
    collapseFiles,
  );

  const handleShareeRoleIdsChange = (roleIds: number[]) => {
    markShareesLocal(roleIds);
    setShareeRoleIds(roleIds);
  };

  // Closing the Share card removes all selected teammates (clearing shareeRoleIds cascades to
  // shareShipment=false in the container) rather than just hiding the card. Otherwise sharees that
  // were carried over from a copied shipment would persist into review and posting.
  const handleShareClose = () => {
    setShareeRoleIds([]);
    collapseShare();
  };

  const handleFilesChange = (newFiles: File[]) => {
    markFilesLocal(newFiles);
    onValueChanged<File[]>("files", newFiles);
  };

  return (
    <Stack direction="column" width="100%">
      {/* New Consolidated Shipment Visibility Component */}
      <ConsolidatedShipmentVisibilityContainer
        notificationSettingsType={shipmentNotificationType}
        selectedCarriers={selectedCarriers}
        partnerGroups={formState.partnerGroups}
        networkInviteEmails={formState.networkInviteEmails}
        setShipmentNotificationType={setShipmentNotificationType}
        setShipmentNotificationEmails={setShipmentNotificationEmails}
        setPartnerGroups={setPartnerGroups}
        setNetworkInviteEmails={setNetworkInviteEmails}
        onEmailInputRef={onEmailInputRef}
        directBooking={directBooking}
        onDirectBookingSelected={onDirectBookingSelected}
        onDirectBookingCleared={onDirectBookingCleared}
        isProject={shipmentType === "MULTI_SHIPMENT_PROJECT"}
      />
      <SectionDivider />
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6">
          {shipmentType === "MULTI_SHIPMENT_PROJECT" ? "Project" : "Shipment"}{" "}
          Details
        </Typography>
      </Stack>
      <Stack direction="row" spacing={2} mt="1rem">
        <Box width="50%">
          <ShipmentTypeMenu
            onAvailableLoadsChanged={(val) =>
              onValueChanged<number | null>("availableLoads", val)
            }
            availableLoads={availableLoads}
            shipmentType={shipmentType}
            onShipmentTypeChanged={(val: ShipmentType) =>
              onValueChanged<ShipmentType>("shipmentType", val)
            }
            shipmentNotificationType={shipmentNotificationType}
            quotesOnlyDisabledReason={quotesOnlyDisabledReason}
          />
        </Box>
        <Box width="50%">
          <PricingUnitOfMeasureMenu
            quotingUnitOfMeasure={quotingUnitOfMeasure}
            setQuotingUnitOfMeasure={(val: UnitOfMeasureType) =>
              onValueChanged<UnitOfMeasureType>("quotingUnitOfMeasure", val)
            }
          />
        </Box>
      </Stack>
      <Stack direction="row" spacing={2} mt="1rem">
        <Box width="50%">
          <UpdatePaymenInfoLabel />
          <EquipmentTypeMultiSelect
            onChange={onEquipmentTypeChanged}
            defaultValue={equipmentTypes}
            value={equipmentTypes}
            width="100%"
          />
          {equipmentError !== null && (
            <FormHelperText error>{equipmentError}</FormHelperText>
          )}
        </Box>
        <Box width="50%">
          <ServiceTypeSelect
            onChange={onServiceChanged}
            defaultValue={serviceType}
            value={serviceType}
          />
        </Box>
      </Stack>
      <Stack direction="row" spacing={2} mt="1rem">
        <Box width="50%">
          <FreetextCommodityInput
            onValueChanged={onFreetextCommodityChanged}
            value={commodityFreetext}
          />
        </Box>
        <Box width="50%">
          <NumberInput
            inputLabel="Weight"
            value={displayWeight}
            min={0}
            onChange={handleWeightChange}
            endAdornment={
              <InputAdornment position="end">
                <Select
                  value={weightUnit}
                  onChange={(e: SelectChangeEvent) =>
                    handleWeightUnitChange(e.target.value as "lb" | "kg")
                  }
                  variant="standard"
                  sx={{
                    "&::before": { display: "none" },
                    "&::after": { display: "none" },
                  }}
                >
                  <MenuItem value="lb">lb</MenuItem>
                  <MenuItem value="kg">kg</MenuItem>
                </Select>
              </InputAdornment>
            }
          />
        </Box>
      </Stack>
      <Stack spacing={2} mt="1rem">
        {specialRequirementsExpanded && (
          <AdvancedOptionCard
            label="Requirements"
            labelTooltip="Straps, tarps, insurance requirements, etc."
            onClose={handleSpecialRequirementsClose}
            color="default"
          >
            <TextField
              fullWidth
              multiline
              minRows={1}
              placeholder="Straps, tarps, insurance requirements, etc."
              value={specialRequirements}
              onChange={(e) => handleSpecialRequirementsChange(e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true }}
              autoFocus={specialRequirementsAutoFocus}
            />
          </AdvancedOptionCard>
        )}
        {additionalDetailsExpanded && (
          <AdvancedOptionCard
            label="Dimensions & Notes"
            labelTooltip="Product dimensions, number of items, stackable/non-stackable, etc."
            onClose={handleAdditionalDetailsClose}
            color="default"
          >
            <TextField
              fullWidth
              multiline
              minRows={1}
              placeholder="Enter dimensions and notes..."
              value={additionalDetails}
              onChange={(e) => handleAdditionalDetailsChange(e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true }}
              autoFocus={additionalDetailsAutoFocus}
            />
          </AdvancedOptionCard>
        )}
        {referenceExpanded && (
          <AdvancedOptionCard
            label="Reference #"
            onClose={handleReferenceClose}
            color="default"
          >
            <TextField
              fullWidth
              placeholder="Reference value"
              value={customName}
              onChange={(e) => handleCustomNameChange(e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true }}
              autoFocus={referenceAutoFocus}
            />
          </AdvancedOptionCard>
        )}
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={1} mt="1rem">
        {!specialRequirementsExpanded && (
          <Tooltip title="Add special handling requirements for carriers">
            <Button
              variant="outlined"
              onClick={expandSpecialRequirements}
              startIcon={<AddIcon />}
              sx={pillButtonSx}
            >
              Requirements
            </Button>
          </Tooltip>
        )}
        {!additionalDetailsExpanded && (
          <Tooltip title="Add dimensions and notes about this shipment">
            <Button
              variant="outlined"
              onClick={expandAdditionalDetails}
              startIcon={<AddIcon />}
              sx={pillButtonSx}
            >
              Dims & Notes
            </Button>
          </Tooltip>
        )}
        {!referenceExpanded && (
          <Tooltip title="Add a reference number for this shipment">
            <Button
              variant="outlined"
              onClick={expandReference}
              startIcon={<AddIcon />}
              sx={pillButtonSx}
            >
              Reference #
            </Button>
          </Tooltip>
        )}
      </Stack>

      <SectionDivider />
      <Box>
        <Typography variant="h6">Shipment Route</Typography>
        <Stack py="1rem" spacing={2}>
          {routePoints.slice(0, -1)}
          <Box>
            <Button
              variant="text"
              startIcon={<AddIcon />}
              onClick={toggleCreateRouteOpen}
              sx={{ textTransform: "none" }}
            >
              Add stop
            </Button>
          </Box>
          {routePoints.slice(-1)}
        </Stack>
      </Box>
      <SectionDivider />
      <Typography variant="h6">Advanced Options</Typography>
      <Stack spacing={2} mt="1rem">
        <AdvancedOptionCard
          label="Notify Customer(s)"
          labelTooltip="Customers will receive live tracking updates as your shipment moves"
          color="primary"
          showCloseButton={false}
        >
          {shipmentTypeSupportsPublicSharing(shipmentType) && (
            <Typography variant="body2" color="text.secondary" mb="0.75rem">
              You can also share a public tracking link with anyone after
              posting — no account required.
            </Typography>
          )}
          <UnifiedCustomerSelectorContainer
            onCustomerSelectionChange={onCustomerSelectionChange}
            initialCustomerRoleIds={formState.customerRoleIds}
            initialExternalCustomerIds={formState.externalCustomerIds}
            initialCustomerInviteEmails={formState.customerInviteEmails}
            initialCustomerSnapshots={formState.customerSnapshots}
            hideBorder
          />
        </AdvancedOptionCard>
        {showBookNow && bookNowExpanded && (
          <AdvancedOptionCard
            label="Book Now Price"
            labelTooltip={
              shipmentType === "MULTI_SHIPMENT_PROJECT"
                ? "Your partners will be able to instantly book loads from this project at this price per load."
                : "Your partners will be able to instantly book the load at this price."
            }
            onClose={handleBookNowClose}
            color="default"
            showCloseButton={false}
            sx={{ "&:hover": { borderColor: "primary.main" } }}
          >
            <BookNowMenu
              currency={bookNowCurrency}
              price={bookNowPrice}
              setCurrency={(val: string) =>
                onValueChanged<string>("bookNowCurrency", val)
              }
              setPrice={handleBookNowPriceChange}
              shipmentType={shipmentType}
              quotingUnitOfMeasure={quotingUnitOfMeasure}
              onClose={handleBookNowClose}
              autoFocus={bookNowAutoFocus}
            />
          </AdvancedOptionCard>
        )}
        {showShareMenu && shareExpanded && (
          <AdvancedOptionCard
            label="Share with Teammates"
            labelTooltip="Company and Branch administrators will automatically be able to view and manage this shipment."
            onClose={handleShareClose}
            color="default"
            showCloseButton={false}
            sx={{ "&:hover": { borderColor: "primary.main" } }}
          >
            <ShareShipmentMenu
              setShareeRoleIds={handleShareeRoleIdsChange}
              selectedSharees={selectedSharees}
              onClose={handleShareClose}
              isProject={shipmentType === "MULTI_SHIPMENT_PROJECT"}
              autoFocus={shareAutoFocus}
            />
          </AdvancedOptionCard>
        )}
        {filesExpanded && (
          <AdvancedOptionCard
            label="Documents"
            labelTooltip="Upload documents related to this shipment. Supported file extensions: .docx, .doc, .txt, .pdf, .jpg, .jpeg, .png, .gif, .bmp, .svg, .webp, .tif, .tf"
            onClose={handleFilesClose}
            color="default"
            showCloseButton={false}
            sx={{
              pt: 2,
              "&:hover": { borderColor: "primary.main", cursor: "pointer" },
            }}
          >
            <FileUpload
              files={files}
              onFilesChange={handleFilesChange}
              onFileErrors={onFileErrors}
              onClose={handleFilesClose}
            />
          </AdvancedOptionCard>
        )}
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={1} mt="1rem">
        {showBookNow && !bookNowExpanded && (
          <Tooltip title="Set a price that carriers can use to instantly book this shipment">
            <Button
              variant="outlined"
              onClick={expandBookNow}
              startIcon={<AddIcon />}
              sx={pillButtonSx}
            >
              Book Now
            </Button>
          </Tooltip>
        )}
        {showShareMenu && !shareExpanded && (
          <Tooltip title="Allow specific company members to manage this shipment">
            <Button
              variant="outlined"
              onClick={expandShare}
              startIcon={<AddIcon />}
              sx={pillButtonSx}
            >
              Share with Teammates
            </Button>
          </Tooltip>
        )}
        {!filesExpanded && (
          <Tooltip title="Attach documents or images to this shipment">
            <Button
              variant="outlined"
              onClick={expandFiles}
              startIcon={<AddIcon />}
              sx={pillButtonSx}
            >
              Add Files
            </Button>
          </Tooltip>
        )}
      </Stack>
      <SectionDivider />
      <Typography variant="h6">Notification Preferences</Typography>
      <Box mb="2rem">
        <EmailNotificationPreferences
          value={emailNotificationType}
          onChange={(newValue: ShipperEmailNotificationType) =>
            onValueChanged<ShipperEmailNotificationType>(
              "emailNotificationType",
              newValue,
            )
          }
          isProject={shipmentType === "MULTI_SHIPMENT_PROJECT"}
        />
      </Box>

      {!showReviewModal && <ErrorTypography error={error} />}
      <Stack direction="row" alignItems="center">
        {submitLoading ? (
          <CircularProgress sx={{ mr: "1rem" }} />
        ) : (
          <Button
            variant="contained"
            size="large"
            onClick={onSubmit}
            sx={{ mr: ".5rem" }}
          >
            <AddIcon />
            {shipmentType === "MULTI_SHIPMENT_PROJECT"
              ? "Review Project"
              : "Review Shipment"}
          </Button>
        )}
        {createTemplateButton}
      </Stack>

      <ShipmentReviewModal
        open={showReviewModal}
        onClose={onReviewModalClose}
        onConfirm={onConfirmSubmit}
        formState={formState}
        submitLoading={submitLoading}
        error={error}
        supplierCustomers={supplierCustomers}
      />
    </Stack>
  );
}
